import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, AlertTriangle } from 'lucide-react';
import { supabase } from '../supabase/client';

interface AuthConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
}

export default function AuthConfirmModal({
  isOpen,
  title,
  message,
  onConfirm,
  onCancel
}: AuthConfirmModalProps) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);

  const handleVerify = async () => {
    if (!password.trim()) {
      setError('请输入鉴权密码');
      return;
    }

    setVerifying(true);
    setError('');

    try {
      // 从 localStorage 获取管理员 admin_id（AdminLayout 用 localStorage 管理登录状态）
      const adminSession = localStorage.getItem('admin_session');
      let adminId: string | undefined;
      if (adminSession) {
        const sessionData = JSON.parse(adminSession);
        // 通过 username 查询 admin_users 表获取 admin_id
        const { data: adminData } = await supabase
          .from('admin_users')
          .select('id')
          .eq('username', sessionData.username)
          .maybeSingle();
        if (adminData) {
          adminId = adminData.id;
        }
      }

      if (!adminId) {
        setError('无法获取管理员信息，请重新登录');
        setVerifying(false);
        return;
      }

      // 调用云函数验证密码，传入 admin_id
      const { data, error: verifyError } = await supabase.functions.invoke('edu-service', {
        body: { action: 'verify_auth_password', password, admin_id: adminId }
      });

      if (verifyError || !data?.success) {
        const errorMsg = data?.message || verifyError?.message || '鉴权密码错误';
        console.error('Auth verification failed:', { data, verifyError, errorMsg });
        setError(errorMsg);
        setVerifying(false);
        return;
      }

      // 验证通过，执行删除操作
      await onConfirm();
      setPassword('');
    } catch (err: any) {
      setError(err.message || '验证失败，请稍后重试');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={onCancel}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 头部 */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
            </div>

            {/* 消息 */}
            <p className="text-gray-600 mb-4">
              {message.split(/(".*?")/).map((part, index) => {
                if (part.startsWith('"') && part.endsWith('"')) {
                  // 引号中的内容（学员姓名）突出显示
                  return (
                    <span key={index} className="text-blue-600 font-bold text-lg mx-1">
                      {part.slice(1, -1)}
                    </span>
                  );
                }
                return <span key={index}>{part}</span>;
              })}
            </p>

            {/* 密码输入 */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                请输入鉴权密码
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !verifying) {
                      handleVerify();
                    }
                  }}
                  placeholder="请输入您的鉴权密码"
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  autoFocus
                />
              </div>
              {error && (
                <p className="text-sm text-red-600 mt-1">{error}</p>
              )}
            </div>

            {/* 按钮 */}
            <div className="flex justify-end gap-3">
              <button
                onClick={onCancel}
                disabled={verifying}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              >
                取消
              </button>
              <button
                onClick={handleVerify}
                disabled={verifying || !password.trim()}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {verifying ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    验证中...
                  </>
                ) : (
                  '确认删除'
                )}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
